import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';

const LAST_UPDATED = '29 Agustus 2026';

// ponytail: static content in one array. Move to CMS/markdown only if legal starts editing this often.
const sections = [
  {
    title: '1. Penerimaan Ketentuan',
    body: [
      'Syarat dan Ketentuan ini mengatur penggunaan situs iplsistem.id, aplikasi Sistem IPL, dan seluruh layanan yang kami sediakan ("Layanan"). Dengan mengakses situs, mengisi formulir, mengunduh aplikasi, atau berlangganan paket kami, Anda menyatakan telah membaca, memahami, dan terikat pada ketentuan ini.',
      'Bila Anda menyetujui ketentuan ini mewakili pengurus RT, RW, atau badan pengelola kompleks, Anda menyatakan berwenang mengikat pihak tersebut.'
    ]
  },
  {
    title: '2. Definisi',
    list: [
      '"Kami" adalah pengelola Sistem IPL.',
      '"Pelanggan" adalah pengurus atau badan pengelola kompleks yang berlangganan Layanan.',
      '"Pengguna" adalah setiap orang yang mengakses situs atau aplikasi, termasuk warga yang memakai aplikasi untuk membayar iuran.',
      '"IPL" adalah Iuran Pemeliharaan Lingkungan yang dikelola melalui Layanan ini.'
    ]
  },
  {
    title: '3. Ruang Lingkup Layanan',
    body: [
      'Sistem IPL adalah perangkat lunak berbasis langganan untuk mencatat, menagih, dan melaporkan iuran lingkungan. Layanan tersedia dalam paket Shared Tier 1, Shared Tier 2, dan Dedicated, dengan cakupan fitur sesuai yang tercantum pada halaman Harga saat berlangganan.'
    ],
    after: [
      'Kami berperan sebagai penyedia perangkat lunak, bukan sebagai pengelola keuangan kompleks. Keputusan mengenai besaran iuran, penggunaan dana, dan hubungan antara pengurus dengan warga sepenuhnya menjadi tanggung jawab Pelanggan.'
    ]
  },
  {
    title: '4. Akun dan Keamanan',
    list: [
      'Pelanggan bertanggung jawab menjaga kerahasiaan kata sandi dan seluruh aktivitas yang terjadi pada akunnya.',
      'Data yang Anda masukkan harus benar, terkini, dan Anda berhak memasukkannya. Untuk data warga, Pelanggan wajib memastikan telah memperoleh dasar yang sah untuk memprosesnya.',
      'Segera beri tahu kami bila Anda menduga terjadi akses tanpa izin pada akun Anda.'
    ]
  },
  {
    title: '5. Berlangganan, Harga, dan Pembayaran',
    list: [
      'Harga setiap paket disampaikan melalui penawaran resmi dari tim kami. Harga dapat berbeda menurut jumlah unit dan kebutuhan teknis kompleks Anda.',
      'Langganan berjalan untuk periode yang disepakati dan dapat diperpanjang atas persetujuan kedua pihak.',
      'Pembayaran dilakukan melalui kanal resmi yang kami informasikan. Kami tidak pernah meminta pembayaran ke rekening pribadi di luar kanal resmi tersebut.',
      'Perubahan harga untuk periode berikutnya akan kami sampaikan paling lambat 30 hari sebelum periode berjalan berakhir.'
    ]
  },
  {
    title: '6. Keterlambatan dan Penghentian Layanan',
    body: [
      'Bila pembayaran melewati jatuh tempo, kami akan mengirim pengingat terlebih dahulu. Layanan dapat kami tangguhkan setelah pemberitahuan tertulis dan tenggang waktu yang wajar.',
      'Setelah langganan berakhir, Pelanggan dapat meminta ekspor data kompleksnya dalam kurun 30 hari sejak tanggal berakhir. Lewat masa tersebut, data dapat kami hapus dari sistem aktif.'
    ]
  },
  {
    title: '7. Kewajiban Pengguna',
    body: ['Anda setuju untuk tidak:'],
    list: [
      'Memakai Layanan untuk tujuan melanggar hukum, penipuan, atau pencucian uang.',
      'Mencoba mengakses akun, data, atau bagian sistem yang bukan hak Anda.',
      'Menyalin, membongkar, atau merekayasa balik perangkat lunak kami.',
      'Mengunggah program berbahaya atau melakukan tindakan yang mengganggu kestabilan Layanan.',
      'Menjual kembali atau menyewakan akses Layanan tanpa persetujuan tertulis dari kami.'
    ]
  },
  {
    title: '8. Data Pelanggan',
    body: [
      'Data kompleks dan data warga yang Anda masukkan tetap menjadi milik Pelanggan. Kami memprosesnya sebatas untuk menjalankan Layanan, sesuai',
    ],
    link: { to: '/kebijakan-privasi', label: 'Kebijakan Privasi' },
    after: [
      'Kami tidak memperjualbelikan data Pelanggan maupun data warga kepada pihak lain.'
    ]
  },
  {
    title: '9. Ketersediaan Layanan',
    body: [
      'Kami berupaya menjaga Layanan tetap tersedia, namun tidak menjanjikan operasi tanpa gangguan. Pemeliharaan terjadwal akan kami informasikan sebelumnya. Jaminan tingkat layanan (SLA) tertulis hanya berlaku bagi paket Dedicated atau bila diperjanjikan tersendiri.'
    ]
  },
  {
    title: '10. Aplikasi Android (APK)',
    body: [
      'Aplikasi Android kami disalurkan melalui berkas APK di situs ini. Unduh hanya dari tautan resmi kami — kami tidak bertanggung jawab atas berkas yang diperoleh dari sumber lain, yang dapat telah dimodifikasi pihak ketiga.',
      'Pemasangan APK memerlukan izin "sumber tidak dikenal" pada perangkat Anda. Aktifkan izin tersebut hanya untuk memasang aplikasi ini, lalu nonaktifkan kembali.'
    ]
  },
  {
    title: '11. Kekayaan Intelektual',
    body: [
      'Seluruh perangkat lunak, kode, desain antarmuka, logo, dan materi pada situs ini adalah milik kami atau pemberi lisensi kami. Langganan memberi Anda hak pakai yang terbatas, tidak eksklusif, dan tidak dapat dialihkan selama masa langganan berjalan — bukan pengalihan kepemilikan.'
    ]
  },
  {
    title: '12. Batasan Tanggung Jawab',
    body: [
      'Layanan disediakan sebagaimana adanya. Sepanjang diizinkan hukum, tanggung jawab kami atas kerugian yang timbul dari penggunaan Layanan dibatasi maksimal sebesar biaya langganan yang Anda bayarkan dalam 12 bulan terakhir.',
      'Kami tidak bertanggung jawab atas kerugian tidak langsung, kehilangan keuntungan, atau kerugian yang timbul dari kekeliruan data yang dimasukkan Pelanggan, penyalahgunaan akun oleh pihak Pelanggan, maupun gangguan jaringan atau perangkat di luar kendali kami.',
      'Pembatasan ini tidak menghapus tanggung jawab yang menurut hukum tidak dapat dikesampingkan.'
    ]
  },
  {
    title: '13. Perubahan Ketentuan',
    body: [
      'Ketentuan ini dapat kami perbarui sewaktu-waktu. Versi terbaru beserta tanggal pembaruannya selalu tersedia di halaman ini. Untuk perubahan yang berdampak besar bagi Pelanggan aktif, kami memberi tahu terlebih dahulu melalui email atau pemberitahuan di aplikasi. Penggunaan Layanan setelah perubahan berlaku berarti Anda menerima versi terbaru.'
    ]
  },
  {
    title: '14. Hukum yang Berlaku',
    body: [
      'Ketentuan ini tunduk pada hukum Republik Indonesia. Setiap perselisihan akan diupayakan diselesaikan secara musyawarah terlebih dahulu. Bila tidak tercapai kesepakatan dalam 30 hari, perselisihan diselesaikan melalui pengadilan negeri di wilayah Jakarta.'
    ]
  },
  {
    title: '15. Hubungi Kami',
    body: ['Pertanyaan mengenai ketentuan ini dapat dikirim ke:'],
    list: [
      'Email: info@iplsistem.id',
      'WhatsApp: 0878-8681-5136',
      'Alamat: Jakarta, Indonesia'
    ]
  }
];

export const TermsOfService = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-brand-50 pt-24 pb-16">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        <Button variant="ghost" onClick={() => navigate('/')} className="mb-8 hover:bg-white/50">
          <ArrowLeft className="mr-2" size={20} />
          Kembali ke Beranda
        </Button>

        <motion.article
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="max-w-3xl mx-auto bg-card rounded-3xl border border-border shadow-lg p-8 sm:p-12"
        >
          <h1 className="text-3xl sm:text-4xl font-bold text-foreground mb-3">
            Syarat & <span className="text-brand-700">Ketentuan</span>
          </h1>
          <p className="text-sm text-foreground/50 mb-10">Terakhir diperbarui: {LAST_UPDATED}</p>

          {sections.map((section) => (
            <section key={section.title} className="mb-9">
              <h2 className="text-xl font-bold text-foreground mb-3">{section.title}</h2>
              {section.body?.map((paragraph, i) => (
                <p key={paragraph} className="text-foreground/70 leading-relaxed mb-3">
                  {paragraph}
                  {section.link && i === section.body.length - 1 && (
                    <>
                      {' '}
                      <Link
                        to={section.link.to}
                        className="text-brand-700 underline font-medium hover:text-brand-800"
                      >
                        {section.link.label}
                      </Link>
                      .
                    </>
                  )}
                </p>
              ))}
              {section.list && (
                <ul className="list-disc pl-5 space-y-2 mb-3 text-foreground/70 leading-relaxed marker:text-brand-600">
                  {section.list.map((entry) => (
                    <li key={entry}>{entry}</li>
                  ))}
                </ul>
              )}
              {section.after?.map((paragraph) => (
                <p key={paragraph} className="text-foreground/70 leading-relaxed mb-3">
                  {paragraph}
                </p>
              ))}
            </section>
          ))}
        </motion.article>
      </div>
    </div>
  );
};
